import { useMemo } from 'react';
import type {
  RouterNavEntry,
  SidebarNavGroup,
} from '@/routing/nav-from-router';
import { groupNavEntries } from '@/routing/nav-from-router';
import { useSidebarPreferences } from '@/hooks/useSidebarPreferences';
import {
  reconcileNavLayout,
  isPinnedEntry,
  sortedPinnedEntries,
  type ReconciledLayout,
} from '@/routing/reconcile-nav-layout';

export type NavLayoutResult = {
  pinnedItems: RouterNavEntry[];
  sections: SidebarNavGroup[];
  isCustomized: boolean;
};

export function useNavLayout(entries: RouterNavEntry[]): NavLayoutResult {
  const { layout, hiddenItems: legacyHidden } = useSidebarPreferences();

  return useMemo(() => {
    if (layout) {
      const reconciled: ReconciledLayout = reconcileNavLayout(layout, entries);
      return { ...reconciled, isCustomized: true };
    }

    const pinnedItems = sortedPinnedEntries(entries);
    // `defaultHidden` routes stay out of the sidebar until a user explicitly
    // adds them (which persists a layout, taking the branch above).
    const visibleEntries = entries.filter(
      (e) =>
        !isPinnedEntry(e) &&
        !e.meta.nav?.defaultHidden &&
        !legacyHidden.includes(e.id),
    );
    const sections = groupNavEntries(visibleEntries);

    return { pinnedItems, sections, isCustomized: false };
  }, [layout, entries, legacyHidden]);
}
